import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SidebarProvider } from "@/contexts/SidebarContext";
import { useSessionRefresh } from "@/hooks/useSessionRefresh";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { OfflineIndicator } from "@/components/layout/OfflineIndicator";
import { ScrollToTopOnNavigation } from "@/components/layout/ScrollToTopOnNavigation";

// Eagerly load Auth page since it's the entry point for unauthenticated users
import Auth from "./pages/Auth";

// Lazy load other pages for code splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const PlayerProfile = lazy(() => import("./pages/PlayerProfile"));
const Schedule = lazy(() => import("./pages/Schedule"));
const Load = lazy(() => import("./pages/Load"));
const Invite = lazy(() => import("./pages/Invite"));
const Settings = lazy(() => import("./pages/Settings"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin pages - Hub & Spoke architecture
const AdminHub = lazy(() => import("./pages/admin/AdminHub"));
const AdminPlayers = lazy(() => import("./pages/admin/AdminPlayers"));
const AdminAnalytics = lazy(() => import("./pages/admin/AdminAnalytics"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminAttendance = lazy(() => import("./pages/admin/AdminAttendance"));
const AdminGroups = lazy(() => import("./pages/admin/AdminGroups"));
const AdminTrainers = lazy(() => import("./pages/admin/AdminTrainers"));
const AdminPayments = lazy(() => import("./pages/admin/AdminPayments"));

// queryClient is now imported from src/lib/queryClient.ts

function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!user) return <Navigate to="/auth" replace />;
  return <>{children}</>;
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAdmin, isTrainer, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isAdmin && !isTrainer) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function PlayerOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isPlayer, loading } = useAuth();
  if (loading) return <LoadingSpinner />;
  if (!isPlayer) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();
  
  // Enable session refresh for active users
  useSessionRefresh();
  
  if (loading) return null;
  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Routes>
        <Route path="/auth" element={user ? <Navigate to="/" replace /> : <Auth />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/invite/:token" element={<Invite />} />
        <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><PlayerOnlyRoute><Profile /></PlayerOnlyRoute></ProtectedRoute>} />
        <Route path="/player/:playerId" element={<ProtectedRoute><AdminRoute><PlayerProfile /></AdminRoute></ProtectedRoute>} />
        <Route path="/schedule" element={<ProtectedRoute><Schedule /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        {/* Admin Hub & Spoke routes */}
        <Route path="/admin" element={<ProtectedRoute><AdminRoute><AdminHub /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/players" element={<ProtectedRoute><AdminRoute><AdminPlayers /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/analytics" element={<ProtectedRoute><AdminRoute><AdminAnalytics /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/reports" element={<ProtectedRoute><AdminRoute><AdminReports /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/attendance" element={<ProtectedRoute><AdminRoute><AdminAttendance /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/groups" element={<ProtectedRoute><AdminRoute><AdminGroups /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/trainers" element={<ProtectedRoute><AdminRoute><AdminTrainers /></AdminRoute></ProtectedRoute>} />
        <Route path="/admin/payments" element={<ProtectedRoute><AdminRoute><AdminPayments /></AdminRoute></ProtectedRoute>} />
        <Route path="/load" element={<ProtectedRoute><AdminRoute><Load /></AdminRoute></ProtectedRoute>} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <LanguageProvider>
      <AuthProvider>
        <SidebarProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <OfflineIndicator />
            <BrowserRouter>
              <ScrollToTopOnNavigation />
              <AppRoutes />
              <InstallPrompt />
            </BrowserRouter>
          </TooltipProvider>
        </SidebarProvider>
      </AuthProvider>
    </LanguageProvider>
  </QueryClientProvider>
);

export default App;
